import { useState, useCallback } from 'react';
import { ToastState } from '../types';

interface UploadFileState {
  name: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
}

interface UseUploadManagerReturn {
  uploadingFiles: Map<string, UploadFileState>;
  handleUploadStart: (files: Array<{ id: string; file: File }>) => void;
  handleUploadComplete: (onRefresh: () => void, setToast: (toast: ToastState) => void) => void;
}

/**
 * Custom hook for managing upload state
 */
export const useUploadManager = (): UseUploadManagerReturn => {
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadFileState>>(new Map());

  const handleUploadStart = useCallback((files: Array<{ id: string; file: File }>) => {
    // Track uploading files
    const newUploadingFiles = new Map(uploadingFiles);
    files.forEach((f) => {
      newUploadingFiles.set(f.id, {
        name: f.file.name,
        progress: 0,
        status: 'uploading' as const,
      });
    });
    setUploadingFiles(newUploadingFiles);
  }, [uploadingFiles]);

  const handleUploadComplete = useCallback((
    onRefresh: () => void,
    setToast: (toast: ToastState) => void
  ) => {
    // Only mark files that are actually completed (not error)
    setUploadingFiles((prev) => {
      const updated = new Map(prev);
      let completedCount = 0;
      let errorCount = 0;
      
      prev.forEach((upload, id) => {
        // Only mark as completed if status is 'uploading' or 'processing' (meaning it succeeded)
        // Files with 'error' status should remain as error
        if (upload.status === 'uploading' || upload.status === 'processing') {
          updated.set(id, { ...upload, status: 'completed' as const, progress: 100 });
          completedCount++;
        } else if (upload.status === 'error') {
          errorCount++;
        }
      });
      
      // Show success toast only if there are actual completed uploads
      if (completedCount > 0) {
        setToast({
          isVisible: true,
          type: 'success',
          message: `${completedCount} video${completedCount > 1 ? 's' : ''} uploaded successfully`
        });
      } else if (errorCount > 0) {
        // Show error toast if all uploads failed
        setToast({
          isVisible: true,
          type: 'error',
          message: `${errorCount} upload${errorCount > 1 ? 's' : ''} failed. Please try again.`
        });
      }
      
      return updated;
    });

    // Refresh current folder and folder tree after upload
    onRefresh();

    // Clear completed uploads from UI after a delay
    setTimeout(() => {
      setUploadingFiles((prev) => {
        const updated = new Map(prev);
        prev.forEach((upload, id) => {
          if (upload.status === 'completed') {
            updated.delete(id);
          }
        });
        return updated;
      });
    }, 3000);
  }, []);

  return {
    uploadingFiles,
    handleUploadStart,
    handleUploadComplete,
  };
};

